import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Standard chrome for an authenticated leaf page.
 *
 * Replaces the recurring `<div className="flex h-full min-h-0 flex-col gap-4 p-6">`
 * boilerplate that customers / users / setup pages were copy-pasting and
 * that timesheets had silently diverged from (no padding + a bordered
 * inner header bar). Use everywhere — exceptions get an opt-out via
 * `className` rather than hand-rolling the wrapper.
 *
 * Owns:
 *   - viewport-height column (`h-full min-h-0`) that scrolls its own content
 *     vertically (`overflow-y-auto`) once it overflows a short viewport — the
 *     app shell above fixes height to `h-svh` and clips (`overflow-hidden`), so
 *     without this a tall page (form/detail/catalog) would be cut off with no
 *     way to reach the bottom.
 *   - 24px breathing room on every side (`p-6`),
 *   - 16px vertical rhythm between PageHeader + content (`gap-4`).
 *
 * Pages that supply their own inner scrollport (`flex-1 min-h-0 overflow-auto`,
 * e.g. `StickyTableScroll` on list pages) are unaffected: that child fills the
 * column exactly, so the shell never overflows and this scroll never engages —
 * the pinned table header keeps working.
 *
 * Pair with `<PageHeader>` for the title row.
 */
export function PageShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-4 overflow-y-auto p-6', className)}>
      {children}
    </div>
  );
}
